import { Component, OnDestroy, OnInit } from '@angular/core';
import { SpinnerService } from './core/providers/spinner/spinner.service';
import { Subscription, Observable, asyncScheduler } from 'rxjs';
import { MessageBusService } from './core/providers/busMessage/messageBusService';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoginModalComponent } from './account/auth/login-modal/login-modal.component';
import { ChangeDetectorRef } from '@angular/core';
import { CrispService } from './core/providers/crisp/crisp.service'; // Importez le service Crisp
import { AuthenticationService } from './core/providers/auth/auth.service'; // Importez le service d'authentification
import { User } from './core/providers/user/users.models'; // Importez le modèle User
import { LocalStorageService } from './core/providers/local-storage/local-storage.service'; // Importez LocalStorageService
import { PlatformLocation } from '@angular/common'; // Importez PlatformLocation
import { tap } from 'rxjs/operators'; // Importez tap
import * as Sentry from '@sentry/angular';
import { filter, observeOn } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { AppContextService } from './core/providers/context/AppContextService ';

@Component({
    selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'vesper';
  isSpinnerVisible = false;
  spinnerState$!: Observable<boolean>;
  subscription: Subscription = new Subscription();
  realTimeControl: any = null;

  constructor(
    public spinnerService: SpinnerService,
    private messageBus: MessageBusService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private crispService: CrispService, // Injectez CrispService
    private authenticationService: AuthenticationService<any>, // Injectez AuthenticationService
    private localStorageService: LocalStorageService, // Injectez LocalStorageService
    private platformLocation: PlatformLocation, // Injectez PlatformLocation
    public appContextService: AppContextService,
    private router: Router
  ) {}

  retryStartup(): void { window.location.reload(); }

  ngOnInit(): void {
    if (this.appContextService.initializationFailed) { return; }
    this.spinnerState$ = this.spinnerService.spinnerState$.pipe(
      observeOn(asyncScheduler)
    );

    this.authenticationService.currentUserInfo().subscribe();

    this.subscription.add(
      this.appContextService.context$.subscribe((context) => {
        this.realTimeControl = context?.realTimeControl ?? null;
      })
    );

    this.subscription.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        this.refreshFirmContext();
      })
    );

    this.subscription.add(
      this.authenticationService.currentUser.subscribe((user: User | null) => {
        const authToken = this.localStorageService.getItem('authToken');

        if (user && authToken) {
          // Vérifier si Crisp doit être chargé avant d'initialiser
          if (this.crispService.shouldLoadCrisp()) {
            this.crispService.initCrisp();
            this.crispService.updateCrisp(user);
            // L'icône Crisp sera affichée automatiquement dans updateCrisp()
            // Suppression de l'ouverture automatique de Crisp
            // this.crispService.showCrisp(); // Commenté pour empêcher l'ouverture automatique
          }
        } else {
          // Masquer l'icône et déconnecter Crisp si l'utilisateur n'est pas connecté
          this.realTimeControl = null;
          this.crispService.shutdownCrisp();
        }
      })
    );

    this.subscription.add(
      this.messageBus.getMessage().subscribe((message) => {
        if (message.type === 'LOGIN_FORM') {
          this.openReconnectionModal();
          this.cdr.detectChanges();
        }
        if (message.type === 'CONTEXT_CHANGED' || message.type === 'REAL_TIME_CONTROL_REFRESH') {
          this.refreshFirmContext();
        }
      })
    );
  }

  get showRealTimeBlocker(): boolean {
    return !!this.localStorageService.getItem('authToken')
      && !!this.realTimeControl?.blocked
      && !this.router.url.startsWith('/planning')
      && !this.router.url.startsWith('/account');
  }

  goToTimeDeclaration(action: 'time' | 'quick'): void {
    this.router.navigate(['/planning'], { queryParams: { timeControl: action } });
  }

  private refreshFirmContext(): void {
    if (!this.localStorageService.getItem('authToken')) return;
    this.appContextService.loadContext().subscribe({ error: () => undefined });
  }

  openReconnectionModal() {
    this.modalService.open(LoginModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // throwError(): void {
  //   Sentry.captureException(new Error('Test error from appComponent button in sentry'));
  // }
}
